// components/features/support/knowledge/KnowledgeArticleForm.tsx
import { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { KnowledgeArticle } from "../../../../types/features/support/KnowledgeArticle";

interface KnowledgeArticleFormProps {
  initialData?: KnowledgeArticle | null;
  onSubmit: (data: Omit<KnowledgeArticle, "id" | "created_at" | "updated_at">) => Promise<void>;
  isLoading?: boolean;
  onCancel?: () => void;
}

const categories = ["OTP & VÉRIFICATION", "CONNEXION", "Procédure Technique", "Escalade", "Réponse Client"];

const departments = ["Support Technique", "Support Client", "KYC", "Paiements", "IT"];

export default function KnowledgeArticleForm({
  initialData,
  onSubmit,
  isLoading = false,
  onCancel,
}: KnowledgeArticleFormProps) {
  const [formData, setFormData] = useState<Omit<KnowledgeArticle, "id" | "created_at" | "updated_at">>({
    title: "",
    slug: "",
    category: "",
    objective: "",
    description: "",
    causes: [],
    signs: [],
    agent_procedure: [],
    technical_procedure: [],
    errors_to_avoid: [],
    escalation: { to: "", when: [] },
    client_response: "",
    product_doc: "",
    responsible_department: "",
    keywords: [],
    is_active: true,
  });

  const [inputFields, setInputFields] = useState({
    causes: "",
    signs: "",
    agent_procedure: "",
    technical_procedure: "",
    errors_to_avoid: "",
    escalation_when: "",
    keywords: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        slug: initialData.slug || "",
        category: initialData.category || "",
        objective: initialData.objective || "",
        description: initialData.description || "",
        causes: initialData.causes || [],
        signs: initialData.signs || [],
        agent_procedure: initialData.agent_procedure || [],
        technical_procedure: initialData.technical_procedure || [],
        errors_to_avoid: initialData.errors_to_avoid || [],
        escalation: initialData.escalation || { to: "", when: [] },
        client_response: initialData.client_response || "",
        product_doc: initialData.product_doc || "",
        responsible_department: initialData.responsible_department || "",
        keywords: initialData.keywords || [],
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleArrayAdd = (field: keyof typeof formData, value: string) => {
    if (value.trim()) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] as string[]), value.trim()],
      }));
      setInputFields((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleArrayRemove = (field: keyof typeof formData, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index),
    }));
  };

  const handleEscalationChange = (field: "to" | "when", value: string) => {
    setFormData((prev) => ({
      ...prev,
      escalation: { ...prev.escalation, [field]: value },
    }));
  };

  const handleEscalationWhenAdd = () => {
    const value = inputFields.escalation_when;
    if (value.trim()) {
      setFormData((prev) => ({
        ...prev,
        escalation: {
          ...prev.escalation,
          when: [...prev.escalation.when, value.trim()],
        },
      }));
      setInputFields((prev) => ({ ...prev, escalation_when: "" }));
    }
  };

  const handleEscalationWhenRemove = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      escalation: {
        ...prev.escalation,
        when: prev.escalation.when.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Générer le slug automatiquement
    const slug = formData.title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

    await onSubmit({ ...formData, slug });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Titre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Ex: OTP NON REÇU"
        />
      </div>

      {/* Catégorie */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Sélectionner une catégorie</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Objectif */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Objectif</label>
        <input
          type="text"
          name="objective"
          value={formData.objective}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Ex: Aider l'utilisateur à recevoir son code OTP"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Description du problème..."
        />
      </div>

      {/* Causes possibles */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Causes possibles</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.causes}
            onChange={(e) => setInputFields((prev) => ({ ...prev, causes: e.target.value }))}
            placeholder="Ajouter une cause..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleArrayAdd("causes", inputFields.causes))}
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("causes", inputFields.causes)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.causes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.causes.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                {item}
                <button type="button" onClick={() => handleArrayRemove("causes", idx)} className="text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Signes d'identification */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Signes d'identification</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.signs}
            onChange={(e) => setInputFields((prev) => ({ ...prev, signs: e.target.value }))}
            placeholder="Ajouter un signe..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleArrayAdd("signs", inputFields.signs))}
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("signs", inputFields.signs)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.signs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.signs.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                {item}
                <button type="button" onClick={() => handleArrayRemove("signs", idx)} className="text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Procédure Agent */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Procédure Agent</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.agent_procedure}
            onChange={(e) => setInputFields((prev) => ({ ...prev, agent_procedure: e.target.value }))}
            placeholder="Ajouter une étape..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleArrayAdd("agent_procedure", inputFields.agent_procedure))
            }
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("agent_procedure", inputFields.agent_procedure)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.agent_procedure.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.agent_procedure.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                {item}
                <button
                  type="button"
                  onClick={() => handleArrayRemove("agent_procedure", idx)}
                  className="text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Procédure Technique */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Procédure Technique</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.technical_procedure}
            onChange={(e) => setInputFields((prev) => ({ ...prev, technical_procedure: e.target.value }))}
            placeholder="Ajouter une étape..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) =>
              e.key === "Enter" &&
              (e.preventDefault(), handleArrayAdd("technical_procedure", inputFields.technical_procedure))
            }
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("technical_procedure", inputFields.technical_procedure)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.technical_procedure.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.technical_procedure.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                {item}
                <button
                  type="button"
                  onClick={() => handleArrayRemove("technical_procedure", idx)}
                  className="text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Erreurs à éviter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Erreurs à éviter</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.errors_to_avoid}
            onChange={(e) => setInputFields((prev) => ({ ...prev, errors_to_avoid: e.target.value }))}
            placeholder="Ajouter une erreur à éviter..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleArrayAdd("errors_to_avoid", inputFields.errors_to_avoid))
            }
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("errors_to_avoid", inputFields.errors_to_avoid)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.errors_to_avoid.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.errors_to_avoid.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                {item}
                <button
                  type="button"
                  onClick={() => handleArrayRemove("errors_to_avoid", idx)}
                  className="text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Escalade */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Escalade - Vers</label>
          <select
            value={formData.escalation.to}
            onChange={(e) => handleEscalationChange("to", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">Sélectionner</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Escalade - Conditions</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={inputFields.escalation_when}
              onChange={(e) => setInputFields((prev) => ({ ...prev, escalation_when: e.target.value }))}
              placeholder="Ajouter une condition..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleEscalationWhenAdd())}
            />
            <button
              type="button"
              onClick={handleEscalationWhenAdd}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {formData.escalation.when.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.escalation.when.map((item, idx) => (
                <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                  {item}
                  <button type="button" onClick={() => handleEscalationWhenRemove(idx)} className="text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Réponse officielle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Réponse officielle au client</label>
        <textarea
          name="client_response"
          value={formData.client_response}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Message officiel à donner au client..."
        />
      </div>

      {/* Documentation Produit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Documentation Produit Associée</label>
        <textarea
          name="product_doc"
          value={formData.product_doc}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Liens ou références vers la documentation produit..."
        />
      </div>

      {/* Département Responsable */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Département Responsable</label>
        <select
          name="responsible_department"
          value={formData.responsible_department}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Sélectionner</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      </div>

      {/* Mots-clés */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Mots-clés (tags)</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputFields.keywords}
            onChange={(e) => setInputFields((prev) => ({ ...prev, keywords: e.target.value }))}
            placeholder="Ajouter un mot-clé..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleArrayAdd("keywords", inputFields.keywords))
            }
          />
          <button
            type="button"
            onClick={() => handleArrayAdd("keywords", inputFields.keywords)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.keywords.map((item, idx) => (
              <span
                key={idx}
                className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
              >
                {item}
                <button type="button" onClick={() => handleArrayRemove("keywords", idx)} className="text-blue-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actif */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-green-500"
        />
        <label className="text-sm font-medium text-gray-700">Article actif</label>
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? "Enregistrement..." : initialData ? "Modifier" : "Créer"}
        </button>
      </div>
    </form>
  );
}
